import { useAuthenticator } from '@aws-amplify/ui-react';
import posthog from 'posthog-js';
import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  initializePostHog,
  posthogConfig,
  environment,
  isProduction,
} from '../config/posthog-config';
import { isBenignGlobalErrorMessage } from '../utils/benignErrors';

interface PostHogContextType {
  posthog: typeof posthog;
  identify: (userId: string, properties?: Record<string, any>) => void;
  capture: (event: string, properties?: Record<string, any>) => void;
  reset: () => void;
}

export const PostHogContext = createContext<PostHogContextType | undefined>(undefined);

interface PostHogProviderProps {
  children: ReactNode;
}

export const PostHogProvider: React.FC<PostHogProviderProps> = ({ children }) => {
  const location = useLocation();

  // Get auth data - useAuthenticator handles memoization internally
  const { user, authStatus } = useAuthenticator((context) => [context.user, context.authStatus]);

  // Initialize PostHog on mount
  useEffect(() => {
    if (!isProduction) {
      console.log('PostHogProvider: Initializing PostHog...');
    }
    initializePostHog();

    // Add global error handler with enhanced tracking
    const handleError = (event: ErrorEvent) => {
      if (isBenignGlobalErrorMessage(event.message)) {
        return;
      }

      // Get additional context
      const errorContext = {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        userAgent: navigator.userAgent,
        url: window.location.href,
        pathname: window.location.pathname,
        timestamp: new Date().toISOString(),
      };

      if (!isProduction) {
        console.error('Global error caught:', event.error);
        console.error('Error details:', errorContext);
      }

      if (posthog.__loaded) {
        posthog.capture('$exception', {
          $exception_message: event.message,
          $exception_type: event.error?.name || 'Error',
          $exception_stack: event.error?.stack,
          $exception_filename: event.filename,
          $exception_lineno: event.lineno,
          $exception_colno: event.colno,
          environment,
          url: errorContext.url,
          pathname: errorContext.pathname,
          userAgent: errorContext.userAgent,
        });

        if (!isProduction) {
          console.log('Error captured in PostHog');
        }
      } else {
        console.warn('PostHog not loaded, cannot capture error');
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const rejectionContext = {
        reason: event.reason,
        reasonMessage: event.reason?.message || String(event.reason),
        reasonStack: event.reason?.stack,
        url: window.location.href,
        pathname: window.location.pathname,
        timestamp: new Date().toISOString(),
      };

      if (!isProduction) {
        console.error('Unhandled promise rejection:', event.reason);
        console.error('Rejection details:', rejectionContext);
      }

      if (posthog.__loaded) {
        posthog.capture('$exception', {
          $exception_message: rejectionContext.reasonMessage,
          $exception_type: 'UnhandledPromiseRejection',
          $exception_stack: rejectionContext.reasonStack,
          environment,
          url: rejectionContext.url,
          pathname: rejectionContext.pathname,
          reason_type: typeof event.reason,
        });

        if (!isProduction) {
          console.log('Promise rejection captured in PostHog');
        }
      } else {
        console.warn('PostHog not loaded, cannot capture promise rejection');
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Track page views
  useEffect(() => {
    if (posthog.__loaded) {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        $pathname: location.pathname,
        environment,
        referrer: document.referrer,
      });
    }
  }, [location]);

  // Identify user when authenticated
  useEffect(() => {
    if (authStatus === 'authenticated' && user && posthog.__loaded) {
      const userId = user.userId || user.username;
      const userProperties = {
        email: user.signInDetails?.loginId,
        username: user.username,
        authStatus: 'authenticated',
        environment,
        loginMethod: user.signInDetails?.loginId ? 'email' : 'username',
      };

      posthog.identify(userId, userProperties);

      if (!isProduction) {
        console.log('User identified in PostHog:', userId);
      }
    } else if (authStatus === 'unauthenticated' && posthog.__loaded) {
      posthog.reset();

      if (!isProduction) {
        console.log('PostHog session reset');
      }
    }
  }, [user, authStatus]);

  const identify = (userId: string, properties?: Record<string, any>) => {
    if (posthog.__loaded) {
      posthog.identify(userId, {
        ...properties,
        environment,
      });
    }
  };

  const capture = (event: string, properties?: Record<string, any>) => {
    if (posthog.__loaded) {
      posthog.capture(event, {
        ...properties,
        environment,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const reset = () => {
    if (posthog.__loaded) {
      posthog.reset();
    }
  };

  const contextValue: PostHogContextType = {
    posthog,
    identify,
    capture,
    reset,
  };

  return <PostHogContext.Provider value={contextValue}>{children}</PostHogContext.Provider>;
};

export const usePostHog = (): PostHogContextType => {
  const context = useContext(PostHogContext);
  if (context === undefined) {
    throw new Error('usePostHog must be used within a PostHogProvider');
  }
  return context;
};
